import { NAV_ITEMS } from '../../app/nav.ts';
import type { GuideDoc, GuideKey } from './types.ts';

type Loader = () => Promise<GuideDoc>;

/**
 * One entry per page. `null` = the guide is not written yet (the dialog shows “กำลังจัดทำคู่มือ”).
 * Docs are lazy-loaded so guide text and capture data never enter the main bundle.
 * To add a guide: write `docs/<menu>.ts` (see docs/modules/guide.md) and replace `null` with a loader.
 */
export const GUIDE_LOADERS: Record<GuideKey, Loader | null> = {
  dashboard: () => import('./docs/dashboard.ts').then((m) => m.default),
  inbox: () => import('./docs/inbox.ts').then((m) => m.default),
  // v1.68.0: screenshots not captured yet (run guide-capture when the page settles).
  savedReplies: null,
  registrations: () => import('./docs/registrations.ts').then((m) => m.guideFor('registrations')),
  registrationsCalendar: () => import('./docs/registrations.ts').then((m) => m.guideFor('registrationsCalendar')),
  registrationsOverview: () => import('./docs/registrations.ts').then((m) => m.guideFor('registrationsOverview')),
  registrationsKits: null,
  campaigns: () => import('./docs/campaigns.ts').then((m) => m.default),
  schedule: () => import('./docs/schedule.ts').then((m) => m.default),
  deliveries: () => import('./docs/deliveries.ts').then((m) => m.default),
  retryCenter: () => import('./docs/retryCenter.ts').then((m) => m.default),
  contacts: () => import('./docs/contacts.ts').then((m) => m.default),
  teams: () => import('./docs/teams.ts').then((m) => m.default),
  events: () => import('./docs/events.ts').then((m) => m.default),
  payments: () => import('./docs/payments.ts').then((m) => m.default),
  folders: () => import('./docs/folders.ts').then((m) => m.default),
  // v1.32.0: guide screenshots not captured yet (run guide-capture when the page is final).
  related: null,
  slips: null,
  telegram: null,
  venue: null,
  segments: () => import('./docs/segments.ts').then((m) => m.default),
  ai: () => import('./docs/bot.ts').then((m) => m.default),
  notifications: () => import('./docs/notifications.ts').then((m) => m.default),
  reports: () => import('./docs/reports.ts').then((m) => m.default),
  connections: () => import('./docs/connections.ts').then((m) => m.default),
  imports: () => import('./docs/imports.ts').then((m) => m.default),
  users: () => import('./docs/users.ts').then((m) => m.default),
  settings: () => import('./docs/settings.ts').then((m) => m.default),
  audit: () => import('./docs/audit.ts').then((m) => m.default),
  // Written once the system-health API is live (screenshots need real data).
  systemHealth: null,
  accountNotifications: () => import('./docs/accountNotifications.ts').then((m) => m.default),
};

export const GUIDE_KEYS = Object.keys(GUIDE_LOADERS) as GuideKey[];

const EXTRA_PAGES: Record<Exclude<GuideKey, (typeof NAV_ITEMS)[number]['key']>, { label: string; description: string; to: string }> = {
  accountNotifications: { label: 'การแจ้งเตือนแชท', description: 'ตั้งค่าเสียงและป๊อปอัปเมื่อมีแชทใหม่ของบัญชีคุณ', to: '/account/notifications' },
};

/** Menu label, description and path for any guide key. */
export function guidePage(key: GuideKey): { label: string; description: string; to: string } {
  const nav = NAV_ITEMS.find((i) => i.key === key);
  if (nav) return { label: nav.label, description: nav.description, to: nav.to };
  return EXTRA_PAGES[key as keyof typeof EXTRA_PAGES];
}

export function hasGuide(key: GuideKey): boolean {
  return GUIDE_LOADERS[key] !== null;
}

export async function loadGuide(key: GuideKey): Promise<GuideDoc | null> {
  const loader = GUIDE_LOADERS[key];
  return loader ? loader() : null;
}
